import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Link2, AlertTriangle } from "lucide-react";
import { tarifasApi, errMsg, fmtMoney, tierLabel } from "./api";
import { PickedPreview } from "./PickedPreview";

const PICK = "__pick__";

export const UnmatchedKimkim = ({ file, provider, extraction, unmatched, onChanged }) => {
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState("");
  if (!unmatched?.length) return null;
  const acts = extraction?.activities || [];

  const link = async (kk) => {
    const key = picks[kk.id];
    const a = acts.find((x) => x.key === key);
    if (!a) return toast.error("Elige la actividad del tarifario");
    setBusy(kk.id);
    try {
      await tarifasApi.createCorrespondence({ provider_id: provider.id, provider_key: a.key, provider_name: a.name, provider_code: a.code, kimkim_activity_id: kk.id, file_id: file.id, status: "pending" });
      toast.success("Vinculada: revísala y confírmala en la lista");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo vincular"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 mb-3" data-testid="unmatched-kimkim">
      <p className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-700" /> {unmatched.length} ficha(s) de KimKim con alias de {provider?.name} sin tarifa asignada</p>
      <p className="text-xs text-muted-foreground">Llevan la marca del proveedor en KimKim pero ninguna actividad de este tarifario se ha emparejado con ellas. Vincúlalas con la línea correcta del tarifario o déjalas sin cambios.</p>
      <div className="divide-y mt-2">
        {unmatched.map((kk) => (
          <div key={kk.id} className="py-2" data-testid={`unmatched-row-${kk.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <strong>{kk.title}</strong>
              <a href={kk.url} target="_blank" rel="noreferrer" className="text-primary underline text-xs flex items-center gap-0.5">abrir en KimKim <ExternalLink className="h-3 w-3" /></a>
              {kk.alias_used && <Badge className="rounded-md bg-blue-50 text-blue-800 hover:bg-blue-50 font-mono">{kk.alias_used}</Badge>}
              <span className="text-xs text-muted-foreground">{kk.pricing_type ? (kk.pricing_type === "per_person_price" ? "por persona" : "por grupo") : "sin precios"}{kk.tiers?.length ? ` · ${kk.tiers.filter((t) => (t.age || "Adult").toLowerCase().startsWith("adult")).map((t) => `${tierLabel(t.min, t.max ?? 99)}: ${fmtMoney(t.rate, "")}`).join(" · ")}` : ""}</span>
            </div>
            {kk.price_notes && <p className="text-xs text-muted-foreground mt-0.5">notas «{kk.price_notes}»</p>}
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Select value={picks[kk.id] || PICK} onValueChange={(v) => setPicks({ ...picks, [kk.id]: v === PICK ? null : v })}>
                <SelectTrigger data-testid={`unmatched-pick-${kk.id}`} className="h-8 rounded-md text-xs w-full md:w-[28rem]"><SelectValue placeholder="Elegir línea del tarifario…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value={PICK}>— elegir línea del tarifario —</SelectItem>
                  {acts.map((a) => <SelectItem key={a.key} value={a.key}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid={`unmatched-link-${kk.id}`} size="sm" className="rounded-md" disabled={!picks[kk.id] || busy === kk.id} onClick={() => link(kk)}><Link2 className="h-3 w-3 mr-1" /> Vincular</Button>
            </div>
            {picks[kk.id] && <PickedPreview fileId={file.id} providerKey={picks[kk.id]} kimkimId={kk.id} testId={`unmatched-preview-${kk.id}`} />}
          </div>
        ))}
      </div>
    </div>
  );
};
